/**
 * 位置相关工具函数
 */

// ==================== 常量定义 ====================

/** 地球半径（米） */
const EARTH_RADIUS_METERS = 6371000;

/** 位置缓存有效期（毫秒）- 10秒 */
const LOCATION_CACHE_DURATION = 10000;

/** 网络定位配置（快速定位，用于200米范围校验） */
const NETWORK_GEOLOCATION_OPTIONS = {
    /** 不使用GPS，只使用网络定位（WiFi/基站/IP），速度快 */
    enableHighAccuracy: false,
    /** 超时时间（毫秒）- 2秒，平衡速度和成功率
     * 网络定位的典型响应时间：
     * - 理想情况：200-500ms（WiFi定位）
     * - 正常情况：500ms-1.5秒（基站定位）
     * - 慢的情况：1.5-3秒（弱信号）
     * 2秒超时既能保证快速响应，又能覆盖大部分正常情况
     */
    timeout: 2000,
    /** 允许使用缓存位置的最大年龄（毫秒）- 60秒，优先使用缓存 */
    maximumAge: 60000
} as const;

/**
 * 说明：
 * 1. 为什么不使用GPS（enableHighAccuracy: true）？
 *    - GPS需要搜索和锁定卫星信号，即使GPS打开也需要几秒到几十秒
 *    - 冷启动（首次定位）：30秒到几分钟
 *    - 热启动（最近定位过）：几秒到十几秒
 *    - 这会导致确认弹框延迟出现，用户体验差
 * 
 * 2. 为什么网络定位足够？
 *    - 网络定位精度：10-100米
 *    - 校验范围：200米
 *    - 即使有误差，对于200米的判断已经足够准确
 *    - 速度：通常500ms-2秒内完成，用户体验好
 * 
 * 3. 如果网络定位失败怎么办？
 *    - 直接使用订单地址经纬度作为fallback
 *    - 不阻塞用户操作，快速响应
 */

// ==================== 类型定义 ====================

/** 位置信息 */
export interface Location {
    latitude: number;
    longitude: number;
    /** 精度（米）- 可选，如果可用 */
    accuracy?: number;
    /** 时间戳（毫秒） */
    timestamp?: number;
}

/** 位置获取结果 */
export interface LocationResult {
    /** 位置信息，如果获取成功 */
    location: Location | null;
    /** 失败原因 */
    failureReason?: 'permission_denied' | 'position_unavailable' | 'timeout' | 'unknown';
}

/** 位置缓存项 */
interface LocationCacheItem {
    location: Location | null;
    timestamp: number;
}

/** 权限状态 */
interface PermissionStatus {
    granted: boolean;
    reason?: string;
}

// ==================== 位置缓存管理器 ====================

    /**
     * 应用级位置缓存管理器
     * 用于在短时间内复用位置信息，减少重复的位置获取
     */
class LocationCacheManager {
    private static cache: LocationCacheItem | null = null;
    /** 正在进行的定位请求 Promise（用于去重，避免并发请求） */
    private static pendingRequest: Promise<LocationResult> | null = null;

    /**
     * 获取缓存的位置
     * @returns 缓存的位置，如果不存在或已过期则返回 null
     */
    static get(): Location | null {
        if (!this.cache) {
            return null;
        }

        const age = Date.now() - this.cache.timestamp;
        if (age > LOCATION_CACHE_DURATION) {
            // 缓存已过期，清除
            this.cache = null;
            return null;
        }

        return this.cache.location;
    }

    /**
     * 设置缓存位置（不缓存失败结果，避免污染缓存）
     * @param location 位置信息
     */
    static set(location: Location | null): void {
        // 不缓存失败结果，避免污染缓存
        if (location === null) {
            return;
        }
        this.cache = {
            location,
            timestamp: Date.now()
        };
    }

    /**
     * 清除缓存
     */
    static clear(): void {
        this.cache = null;
    }

    /**
     * 获取正在进行的定位请求（用于去重）
     */
    static getPendingRequest(): Promise<LocationResult> | null {
        return this.pendingRequest;
    }

    /**
     * 设置正在进行的定位请求
     */
    static setPendingRequest(request: Promise<LocationResult> | null): void {
        this.pendingRequest = request;
    }
}

// ==================== 工具函数 ====================

/**
 * 计算两点之间的直线距离（Haversine公式）
 * @param lat1 纬度1
 * @param lon1 经度1
 * @param lat2 纬度2
 * @param lon2 经度2
 * @returns 距离（米）
 */
export function calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
): number {
    const dLat = toRadians(lat2 - lat1);
    const dLon = toRadians(lon2 - lon1);

    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return EARTH_RADIUS_METERS * c;
}

/**
 * 角度转弧度
 */
function toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
}

// ==================== 权限管理 ====================

/**
 * 检查定位权限状态
 * @returns Promise<PermissionStatus> 权限状态
 */
async function checkLocationPermission(): Promise<PermissionStatus> {
    // 1. 优先使用原生桥接检查权限（如果可用）
    if (typeof window !== 'undefined' && (window as any).SwiftXBridge?.checkPermission) {
        try {
            const status = await (window as any).SwiftXBridge.checkPermission('location');
            return status;
        } catch (error) {
            console.warn('原生权限检查失败，降级到浏览器API:', error);
        }
    }

    // 2. 使用浏览器 Permissions API（如果支持）
    if ('permissions' in navigator) {
        try {
            const result = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
            return {
                granted: result.state === 'granted',
                reason: result.state === 'denied' ? 'permission_denied' : undefined
            };
        } catch (error) {
            console.warn('Permissions API 查询失败:', error);
        }
    }

    // 3. 无法确定权限状态，返回未决定
    return { granted: false, reason: 'unknown' };
}

/**
 * 请求定位权限（通过原生端）
 * @returns Promise<PermissionStatus> 权限状态
 */
async function requestLocationPermission(): Promise<PermissionStatus> {
    // 1. 优先使用原生桥接请求权限（如果可用）
    if (typeof window !== 'undefined' && (window as any).SwiftXBridge?.requestPermission) {
        try {
            const status = await (window as any).SwiftXBridge.requestPermission('location');
            return status;
        } catch (error) {
            console.warn('原生权限请求失败:', error);
            return { granted: false, reason: 'native_request_failed' };
        }
    }

    // 2. 原生桥接不可用，返回失败
    return { granted: false, reason: 'native_bridge_not_available' };
}

// ==================== 位置获取 ====================

/**
 * 使用浏览器 Geolocation API 获取位置
 * @param options 定位选项
 * @returns Promise<LocationResult> 包含位置信息和失败原因
 */
function getLocationWithOptions(
    options: PositionOptions
): Promise<LocationResult> {
    return new Promise((resolve) => {
        if (!navigator.geolocation) {
            resolve({
                location: null,
                failureReason: 'unknown'
            });
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                resolve({
                    location: {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        accuracy: position.coords.accuracy,
                        timestamp: position.timestamp
                    },
                    failureReason: undefined
                });
            },
            (error) => {
                // 错误类型：
                // - PERMISSION_DENIED (1): 用户拒绝权限
                // - POSITION_UNAVAILABLE (2): 位置不可用（有权限但定位失败）
                // - TIMEOUT (3): 超时（有权限但超时）
                const failureReason = error.code === 1 
                    ? 'permission_denied' as const
                    : error.code === 2
                    ? 'position_unavailable' as const
                    : error.code === 3
                    ? 'timeout' as const
                    : 'unknown' as const;
                
                console.error('获取位置失败:', {
                    code: error.code,
                    message: error.message,
                    failureReason,
                    codeMap: {
                        1: 'PERMISSION_DENIED',
                        2: 'POSITION_UNAVAILABLE',
                        3: 'TIMEOUT'
                    }[error.code]
                });
                
                resolve({
                    location: null,
                    failureReason
                });
            },
            options
        );
    });
}

/**
 * 获取当前位置（优化版）
 * 
 * 策略：
 * 1. 检查缓存，如果有效则直接返回
 * 2. 检查权限，如果没有权限则尝试通过原生端请求
 * 3. 先尝试快速网络定位，如果精度不够则升级到GPS
 * 4. 缓存结果
 * 
 * @param options 选项
 * @param options.useCache 是否使用缓存，默认 true
 * @param options.requestPermissionIfDenied 如果权限被拒绝，是否通过原生端请求权限，默认 true
 * @returns Promise<LocationResult> 包含位置信息和失败原因（可以区分无权限和定位失败）
 */
export async function getCurrentLocation(options: {
    useCache?: boolean;
    requestPermissionIfDenied?: boolean;
} = {}): Promise<LocationResult> {
    const { useCache = true, requestPermissionIfDenied = true } = options;

    // 1. 检查缓存
    if (useCache) {
        const cached = LocationCacheManager.get();
        if (cached) {
            return {
                location: cached,
                failureReason: undefined
            };
        }
    }

    // 2. 检查是否有正在进行的定位请求（去重，避免并发请求）
    const pendingRequest = LocationCacheManager.getPendingRequest();
    if (pendingRequest) {
        console.log('检测到正在进行的定位请求，复用该请求以避免并发...');
        try {
            return await pendingRequest;
        } catch (error) {
            console.warn('复用定位请求失败，继续发起新请求:', error);
            // 如果复用失败，继续发起新请求
        }
    }

    // 3. 创建新的定位请求
    const locationRequest = (async (): Promise<LocationResult> => {
        try {
            // 3.1 直接尝试获取位置（不先检查权限，因为Web环境权限检查可能不准确）
            // 优化：直接调用 getCurrentPosition，根据实际错误码判断失败原因
            // 这样更准确，因为：
            // 1. Web环境的权限检查（Permissions API）可能不准确或不支持
            // 2. 直接尝试获取位置，浏览器会返回准确的错误码
            // 3. 如果requestPermissionIfDenied为true，在定位失败后再尝试请求权限
            const result = await getLocationWithOptions(NETWORK_GEOLOCATION_OPTIONS);
            
            // 3.2 如果定位成功，缓存并返回
            if (result.location) {
                if (useCache) {
                    LocationCacheManager.set(result.location);
                }
                return result;
            }

            // 3.3 如果定位失败，根据失败原因处理
            // 如果是权限被拒绝，且允许请求权限，则尝试请求权限后重试
            if (result.failureReason === 'permission_denied' && requestPermissionIfDenied) {
                const permissionStatus = await requestLocationPermission();
                if (permissionStatus.granted) {
                    // 权限已授予，重试获取位置
                    const retryResult = await getLocationWithOptions(NETWORK_GEOLOCATION_OPTIONS);
                    if (retryResult.location) {
                        if (useCache) {
                            LocationCacheManager.set(retryResult.location);
                        }
                        return retryResult;
                    }
                    // 重试仍然失败，返回失败原因
                    return retryResult;
                }
            }

            // 3.4 返回失败结果（包含失败原因）
            return result;
        } finally {
            // 清除正在进行的请求标记
            LocationCacheManager.setPendingRequest(null);
        }
    })();

    // 4. 设置正在进行的请求
    LocationCacheManager.setPendingRequest(locationRequest);

    // 5. 等待请求完成
    return await locationRequest;
}

/**
 * 校验位置是否在范围内
 * @param currentLat 当前位置纬度
 * @param currentLon 当前位置经度
 * @param targetLat 目标位置纬度
 * @param targetLon 目标位置经度
 * @param maxDistance 最大距离（米），默认200
 * @returns 是否在范围内
 * 
 * 规则：
 * - 如果无法获取司机位置，返回true（允许绕过校验，产品设计允许）
 * - 如果订单地址没有经纬度，返回false（理论上不会发生，但作为防御性编程）
 * - 如果两者都存在，计算距离并判断是否在范围内
 */
export function isLocationWithinRange(
    currentLat: number | null,
    currentLon: number | null,
    targetLat: number | null,
    targetLon: number | null,
    maxDistance: number = 200
): boolean {
    // 如果无法获取司机位置，返回true（允许绕过校验，产品设计允许）
    if (currentLat === null || currentLon === null) {
        return true;
    }

    // 如果订单地址没有经纬度，返回false（理论上不会发生，但作为防御性编程）
    if (targetLat === null || targetLon === null) {
        return false;
    }

    // 两者都存在，计算距离并判断是否在范围内
    const distance = calculateDistance(currentLat, currentLon, targetLat, targetLon);
    return distance <= maxDistance;
}

/**
 * 清除位置缓存
 * 在需要强制刷新位置时调用（例如用户手动刷新）
 */
export function clearLocationCache(): void {
    LocationCacheManager.clear();
}

